<script setup lang="ts">
  import {
    computed,
    nextTick,
    onActivated,
    onBeforeUnmount,
    onMounted,
    reactive,
    ref,
    watch,
  } from 'vue';
  import {
    actions as actionTypes,
    useUserComponent,
    useHelpers,
    createComponentScope,
    Resource,
    uniqueID,
  } from '@integro/core';
  import type { IAppHelpers } from '@integro/core';
  import * as _ from 'lodash';

  defineOptions({
    name: 'UcWorksheetInvoiceLineAccountings',
    inheritAttrs: false,
  });

  const componentMetadata = {
    kind: 'usercontrol',
  };

  const emit = defineEmits(['reload', 'close', 'save']);

  const props = defineProps({
    worksheet: {
      type: Object,
      required: true,
    },
    isReadonly: {
      type: Boolean,
      required: false,
    },
  });

  const {
    $app,
    $fn,
    $i18n,
    $store,
    $config,
    $imodal,
    $notification,
    $tabs,
    $route,
    $router,
    gettext,
    pgettext,
    ngettext,
    npgettext,
    schemaDefaults,
    getSchema,
    searchDataSource,
    randomElementId,
    datetime,
    date,
    time,
    number,
    money,
    distance,
    username,
    filesize,
  } = useHelpers();

  const items = ref(null);
  const ux = ref({
    errorHandling: 'local',
    initialLoadSpinner: true,
  });
  const b_5962862420439144_rowHeader = computed(() => false);
  const b_5962862420439144_paging = computed(() => false);
  const b_5962862420439144_sortable = computed(() => false);
  const b_5962862420439144_editable = computed(() => false);
  const b_5962862420439144_columns = computed(() => [
    {
      name: 'invoice_number',
      header: $fn.value.gettext('Invoice Number'),
      formatter: {
        name: 'template',
        value:
          '<div v-if="value" class="text-start">\n\
  <ItpLink :to="{ name: \'app.invoice\', params: {id: row.invoice_id}}">\n\
    {{ value }}\n\
  </ItpLink>\n\
</div>\n\
',
      },
    },
    {
      name: 'product_description',
      header: $fn.value.gettext('Description'),
      formatter: {
        name: 'template',
        value:
          '<div v-if="value" class="text-start">\n\
  <ItpLink :to="{ name: \'app.product\', params: {id: row.product_id}}">\n\
    {{ value }}\n\
  </ItpLink>\n\
</div>\n\
',
      },
    },
    {
      name: 'quantity',
      type: 'number',
      header: $fn.value.gettext('Quantity'),
      width: '150px',
    },
    {
      name: 'unit',
      header: $fn.value.pgettext('abbreviated', 'Unit'),
    },
    {
      name: 'unit_price',
      header: $fn.value.gettext('Unit Price'),
      type: 'number',
    },
    {
      name: 'amount',
      header: $fn.value.gettext('Amount'),
      type: 'number',
    },
    {
      name: 'expense_type',
      header: $fn.value.gettext('Expense Type'),
      formatter: {
        name: 'template',
        value:
          "<div v-if=\"value\">\n\
  {{pgettext('ExpenseType', value, 'Sentence') }}\n\
</div>\n\
",
      },
    },
    {
      name: 'cost_center',
      header: $fn.value.gettext('Cost Center'),
    },
    {
      name: 'handover_date',
      header: $fn.value.gettext('Handover Date'),
      type: 'date',
    },
    {
      name: 'notes',
      header: $fn.value.gettext('Notes'),
    },
  ]);

  const $data = computed(() => ({
    items: items.value,
    ux: ux.value,
    b_5962862420439144_rowHeader: b_5962862420439144_rowHeader.value,
    b_5962862420439144_paging: b_5962862420439144_paging.value,
    b_5962862420439144_sortable: b_5962862420439144_sortable.value,
    b_5962862420439144_editable: b_5962862420439144_editable.value,
    b_5962862420439144_columns: b_5962862420439144_columns.value,
  }));

  const $$load_items = async () => {
    return $fn.value.fetch('query_invoice_line_accounting_for_worksheet', {
      parameters: { id: props.worksheet.id },
    });
  };

  const {
    loading,
    error,
    loaded,
    reloading,
    showLoadingIndicator,
    loadDataThrottled,
    friendlyName,
    loadData,
    touchLastLoaded,
    handleError,
    setData,
    forms,
    registerForm,
    unregisterForm,
    canLoadData,
    isModalComponent,
    modalName,
    pageDocument: _pageDocument,
    routeTab,
    ux: _ux,
    componentMetadata: _componentMetadata,
    $uid,
    lastLoaded,
  } = useUserComponent(props, {
    metadata: componentMetadata,
    dataSpecs: [
      { name: 'items' },
      { name: 'ux' },
      { name: 'b_5962862420439144_rowHeader', nodeId: '5962862420439144' },
      { name: 'b_5962862420439144_paging', nodeId: '5962862420439144' },
      { name: 'b_5962862420439144_sortable', nodeId: '5962862420439144' },
      { name: 'b_5962862420439144_editable', nodeId: '5962862420439144' },
      { name: 'b_5962862420439144_columns', nodeId: '5962862420439144' },
    ],
    loaders: { items: $$load_items },
    ux: { errorHandling: 'local', initialLoadSpinner: true },
    dataRefs: {
      items,
      ux,
      b_5962862420439144_rowHeader,
      b_5962862420439144_paging,
      b_5962862420439144_sortable,
      b_5962862420439144_editable,
      b_5962862420439144_columns,
    },
  });

  const $component = createComponentScope(
    {
      $app,
      $fn,
      $i18n,
      $store,
      $imodal,
      $notification,
      $tabs,
      $router,
    } as IAppHelpers,
    {
      emit,
      forms,
      error,
      isModalComponent,
      modalName,
      componentMetadata,
      loadData,
      registerForm,
      unregisterForm,
      setData,
      touchLastLoaded,
      handleError,
    }
  );

  watch(
    () => props.worksheet,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('worksheet');
    }
  );
  watch(
    () => props.isReadonly,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('isReadonly');
    }
  );

  const act_5962862420439144_script_htvif95sd_value_callable = async (
    $event
  ) => {
    return props.worksheet.id
      ? $event.data.waitFor($fn.value.crud('work_item').upsert($event.data.row))
      : props.worksheet.work_items.push($event.data.row);
  };
  const act_5962862420439144_script_htvif95sd_getActionArgs = async (
    $event
  ) => {
    return {
      value: async () => {
        await act_5962862420439144_script_htvif95sd_value_callable($event);
      },
    };
  };
  const act_5962862420439144_script_wafe0v5xe_value_callable = async (
    $event
  ) => {
    return props.worksheet.id
      ? $event.data.waitFor($fn.value.crud('work_item').delete($event.data.row))
      : _.remove(props.worksheet.work_items, $event.data.row);
  };
  const act_5962862420439144_script_wafe0v5xe_getActionArgs = async (
    $event
  ) => {
    return {
      value: async () => {
        await act_5962862420439144_script_wafe0v5xe_value_callable($event);
      },
    };
  };

  const act_5962862420439144_script_htvif95sd = new actionTypes.ScriptAction({
    actionArgs: act_5962862420439144_script_htvif95sd_getActionArgs,
  });
  const inst_evh_5962862420439144_save_8yaqdm77a =
    new actionTypes.EventHandlerImpl({
      action: act_5962862420439144_script_htvif95sd,
      event: 'save',
      displayName: 'script',
    });
  const inst_evh_5962862420439144_save = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_5962862420439144_save_8yaqdm77a],
    event: 'save',
  });
  const act_5962862420439144_script_wafe0v5xe = new actionTypes.ScriptAction({
    actionArgs: act_5962862420439144_script_wafe0v5xe_getActionArgs,
  });
  const inst_evh_5962862420439144_delete_r95jhbh1e =
    new actionTypes.EventHandlerImpl({
      action: act_5962862420439144_script_wafe0v5xe,
      event: 'delete',
      displayName: 'script',
    });
  const inst_evh_5962862420439144_delete = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_5962862420439144_delete_r95jhbh1e],
    event: 'delete',
  });

  const actions = {
    inst_evh_5962862420439144_save_8yaqdm77a,
    inst_evh_5962862420439144_save,
    inst_evh_5962862420439144_delete_r95jhbh1e,
    inst_evh_5962862420439144_delete,
  };

  const evh_5962862420439144_save_8yaqdm77a = async (event, scope) => {
    return await actions.inst_evh_5962862420439144_save_8yaqdm77a.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_5962862420439144_save = async (event, scope) => {
    return await actions.inst_evh_5962862420439144_save.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_5962862420439144_delete_r95jhbh1e = async (event, scope) => {
    return await actions.inst_evh_5962862420439144_delete_r95jhbh1e.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_5962862420439144_delete = async (event, scope) => {
    return await actions.inst_evh_5962862420439144_delete.executeFromDOM(
      $component,
      event,
      scope
    );
  };

  defineExpose({
    loadData,
    loading,
    loaded,
    error,
  });
</script>

<template>
  <ItpBox direction="column" alignItems="stretch">
    <ItpBox class="pb-2">
      <strong>
        {{ pgettext('Worksheet', 'Invoices') }}
      </strong>
    </ItpBox>
    <ItpDataTable
      rowKey="id"
      tableId="WorksheetInvoiceLineAccountings"
      :rowHeader="b_5962862420439144_rowHeader"
      :paging="b_5962862420439144_paging"
      :sortable="b_5962862420439144_sortable"
      :editable="b_5962862420439144_editable"
      :value="items"
      variant="details"
      :rowTemplate="{ worksheet_id: worksheet.id }"
      :columns="b_5962862420439144_columns"
      class="h-100"
      v-if="loaded"
      @save="evh_5962862420439144_save($event, {})"
      @delete="evh_5962862420439144_delete($event, {})"
    >
    </ItpDataTable>
  </ItpBox>
</template>
